import { NoData } from "@/components/common/section-components/NoData";
import { LoadedTaxesActions } from "@/components/common/section-components/action/LoadedTaxesActions";
import { RequestStatusBadge } from "@/components/common/section-components/badge/RequestStatusBadge";
import { TaxesLoadedFilter } from "@/components/common/section-components/filters/TaxesLoadedFilter";
import { StandardTable } from "@/components/table/StandardTable";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import {
  fetchLoadedTaxesHandler,
  LoadedTaxesFiltersForm,
  submitLoadedTaxesFiltersHandler,
  resendLoadedTaxHandler,
} from "@/pages/config/taxes/schema/handler-functions";
import type {
  GetLoadedTaxesBackendQuery,
  LoadedTaxesListResponse,
} from "@/pages/config/taxes/schema/taxes.schema";
import { isEmpty } from "@/utils/query-string";
import { Flex } from "@chakra-ui/react";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import type { FieldValues } from "react-hook-form";
import { useForm, useWatch } from "react-hook-form";
import type { NavigateFunction } from "react-router-dom";
import { LoadedTaxesStatus } from "@/constants/entity-status";
import dayjs from "dayjs";
import { formatCurrency } from "@/utils/format-currency";

interface Props {
  navigate: NavigateFunction;
  fetchPermissions?: () => Promise<void>;
  currentTab: string;
  loadingTaxes: boolean;
  setLoadingTaxes: Dispatch<SetStateAction<boolean>>;
}

export const LoadedTaxesTab = ({
  navigate,
  fetchPermissions,
  currentTab,
  loadingTaxes,
  setLoadingTaxes,
}: Props) => {
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    setIsActive(true);
  }, []);
  const [loadedTaxes, setLoadedTaxes] =
    useState<LoadedTaxesListResponse | null>(null);

  const [openLoadedFilters, setOpenLoadedFilters] = useState(false);
  const [loadedFiltersApplied, setLoadedFiltersApplied] = useState(false);
  const [currentLoadedFilters, setCurrentLoadedFilters] = useState<
    Partial<GetLoadedTaxesBackendQuery> | undefined
  >({});
  const [page, setPage] = useState(1);

  const {
    control: loadedControl,
    handleSubmit: handleSubmitLoaded,
    reset: resetLoaded,
  } = useForm<LoadedTaxesFiltersForm>();

  const loadedTaxesHeaders = useMemo(
    () => [
      { label: "Fecha", value: "fechaImpuesto", align: "inherit" },
      { label: "Titular", value: "titular", align: "inherit" },
      { label: "Tipo de impuesto", value: "tipoImpuesto", align: "inherit" },
      { label: "Monto", value: "montoFormatted", align: "inherit" },
      { label: "Estado", value: "estado", align: "inherit" },
    ],
    [],
  );

  const fetchLoadedTaxes = async (
    filters?: Partial<GetLoadedTaxesBackendQuery>,
    showLoad: boolean = true,
  ) => {
    await fetchLoadedTaxesHandler({
      setLoading: setLoadingTaxes,
      setItems: setLoadedTaxes,
      navigate,
      fetchPermissions,
      showLoad,
      filters: { ...filters, "P.Records": 10 },
    });
  };

  const handlePageChange = async (newPage: number) => {
    setPage(newPage);
    await fetchLoadedTaxes({ ...currentLoadedFilters, "P.Pagina": newPage });
  };

  useEffect(() => {
    if (isActive) {
      fetchLoadedTaxes({}, true);
    }
  }, [isActive]);

  useEffect(() => {
    if (currentTab !== "ImpuestosCargados") return;
    setPage(1);
    fetchLoadedTaxes(currentLoadedFilters ?? {}, true);
  }, [currentTab]);

  const loadedValues = useWatch({ control: loadedControl });
  const loadedFiltersEmpty = Object.values(loadedValues ?? {}).every((v) => {
    if (v && typeof v === "object" && "value" in v) {
      return isEmpty((v as any).value);
    }
    return isEmpty(v);
  });

  const submitLoadedFilters = async (event: FieldValues) => {
    setPage(1);
    await submitLoadedTaxesFiltersHandler({
      event: event as LoadedTaxesFiltersForm,
      setOpenFilters: setOpenLoadedFilters,
      setFiltersApplied: setLoadedFiltersApplied,
      setCurrentFilters: setCurrentLoadedFilters,
      fetchLoadedTaxes,
    });
  };

  const clearLoadedFilters = async () => {
    setPage(1);
    resetLoaded({
      fecha: undefined,
      cuenta: null,
      tipoImpuesto: [],
      estado: [],
    });
    setLoadedFiltersApplied(false);
    setOpenLoadedFilters(false);
    setCurrentLoadedFilters({});
    await fetchLoadedTaxes({}, true);
  };

  const handleResend = async (id: number) => {
    await resendLoadedTaxHandler({ id, navigate, fetchPermissions });
    await fetchLoadedTaxes(currentLoadedFilters ?? {}, true);
  };

  const getActionElements = (item: any) => {
    if (item?.estadoRaw !== LoadedTaxesStatus.ERROR) return <></>;
    return <LoadedTaxesActions item={item} handleResend={handleResend} />;
  };

  return (
    <Flex flexDir="column" gap="10px">
      <TaxesLoadedFilter
        control={loadedControl as unknown as any}
        handleSubmit={handleSubmitLoaded}
        openFilters={openLoadedFilters}
        setOpenFilters={setOpenLoadedFilters}
        submitFilters={submitLoadedFilters}
        clearFilters={clearLoadedFilters}
        disableFilters={loadingTaxes}
        loadingFilters={loadingTaxes}
        filtersApplied={loadedFiltersApplied}
        disableClear={loadingTaxes}
        disableApply={loadingTaxes || loadedFiltersEmpty}
      />

      <Flex
        flexDir="column"
        overflowY="auto"
        maxHeight="calc(100vh - 200px)"
        paddingBottom="12px"
      >
        {Boolean(loadedTaxes?.listado?.length) && (
          <StandardTable
            items={
              loadedTaxes?.listado?.map((x) => ({
                ...x,
                estadoRaw: x?.estado,
                estado: (
                  <RequestStatusBadge status={x?.estado as LoadedTaxesStatus} />
                ),
                montoFormatted: `$${formatCurrency(x?.monto)}`,
                fechaImpuesto: dayjs(x.fechaImpuesto).format("DD/MM/YYYY"),
              })) ?? []
            }
            headers={loadedTaxesHeaders}
            noSelection
            fullWidth
            actionsHeaderLabel="Reenviar"
            actionElements={getActionElements}
            actionsAlign="center"
            headerLabelAlign="center"
          />
        )}

        {Boolean(loadedTaxes?.totalPages && loadedTaxes.totalPages > 1) && (
          <PaginationComponent
            page={page}
            handlePageChange={handlePageChange}
            pageSize={10}
            count={loadedTaxes?.totalRecords ?? 10}
          />
        )}

        {Boolean(!loadingTaxes && !loadedTaxes?.listado?.length) && <NoData />}
      </Flex>
    </Flex>
  );
};
